import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AgentService } from '../../modules/admin-features/services/agent.service';
import { map, switchMap } from 'rxjs/operators';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { combineLatest, Subscription } from 'rxjs';
import { BoomerFormsModule } from '../../shared/components/boomer-forms/boomer-forms.module';
import { IntakeFormService } from '../../modules/admin-features/services/intake-form.service';
import { MatButton } from '@angular/material/button';
import { IntakeFormInputComponent } from '../intake-form-input/intake-form-input.component';
import { IntakeFormSelectComponent } from '../intake-form-select/intake-form-select.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { IntakeFormInputDateComponent } from '../intake-form-input-date/intake-form-input-date.component';
import { AgentDisplayResponse } from '../../shared/components/applicant-soas/soa.service';
import * as moment from 'moment';
import { ISO_8601 } from 'moment';
import { MatError } from '@angular/material/form-field';
import { IntakeFormCountySelectComponent } from '../intake-form-input/intake-form-county-select/intake-form-county-select.component';

@Component({
  selector: 'app-medicare-intake',
  standalone: true,
  imports: [
    BoomerFormsModule,
    ReactiveFormsModule,
    MatButton,
    IntakeFormInputComponent,
    IntakeFormSelectComponent,
    MatProgressSpinner,
    IntakeFormInputDateComponent,
    MatError,
    IntakeFormCountySelectComponent,
  ],
  templateUrl: './medicare-intake.component.html',
  styleUrl: './medicare-intake.component.scss',
})
export class MedicareIntakeComponent implements OnInit, OnDestroy {
  private intakeFormService = inject(IntakeFormService);
  private agentService = inject(AgentService);
  private route = inject(ActivatedRoute);
  private formBuilder = inject(FormBuilder);
  private router = inject(Router);

  intakeForm: FormGroup;
  agentInfo: AgentDisplayResponse;
  agentExternalId: string;
  private subscription: Subscription;
  step: number;
  saving: boolean = false;

  currentInsuranceType = {
    "I am enrolled in an Employer plan (either through your own employment or a spouse's employment)": 'Employer',
    'I am enrolled in an individual plan, not through an Employer': 'IFP',
    'I am enrolled in COBRA': 'COBRA',
    // tslint:disable-next-line:max-line-length
    "I am enrolled on a retiree plan offered by a former employer (either through your own employment or a spouse's employment)":
      'Retiree',
    'I am enrolled in Medicare': 'Medicare',
    'I am enrolled in Medicaid': 'Medicaid',
    'I do not have any health insurance coverage': 'No Insurance',
    Other: 'Other',
  };

  ngOnInit() {
    this.intakeForm = this.formBuilder.group({
      agentId: [''],
      1: this.formBuilder.group({
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
      }),
      2: this.formBuilder.group({ dob: ['', this.validateDate] }),
      3: this.formBuilder.group({
        phone: ['', Validators.required],
        zip: ['', Validators.required],
        fips: [''],
        county: [''],
        state: ['', Validators.required],
      }),
      4: this.formBuilder.group({ medicareFamiliarity: ['', Validators.required] }),
      5: this.formBuilder.group({ insuranceBeginDate: ['', Validators.required] }),
      6: this.formBuilder.group({ currentInsuranceType: ['', Validators.required] }),
      7: this.formBuilder.group({ currentCarrier: [''] }),
      8: this.formBuilder.group({ enrolledInMedicare: [''] }),
      9: this.formBuilder.group({ additionalInfo: [''] }),
      10: this.formBuilder.group({ referrer: [''] }),
    });
    const agentExternalId$ = this.route.paramMap.pipe(map(params => params.get('agentId')));
    const agentInfo$ = agentExternalId$.pipe(switchMap(agentId => this.agentService.getAgentByDisplayId(agentId)));
    this.subscription = combineLatest([agentExternalId$, agentInfo$]).subscribe(a => {
      this.agentExternalId = a[0];
      this.agentInfo = a[1];
      this.intakeForm.patchValue({ agentId: this.agentExternalId }, { emitEvent: false });
    });
    this.step = 0;
  }

  validateDate(control: AbstractControl<string>): ValidationErrors | null {
    if (moment(control.value, ISO_8601, true).isValid()) {
      return null;
    } else {
      return { date: true };
    }
  }

  formControl(name: string): FormControl {
    return this.intakeForm.get(name) as FormControl;
  }

  nextStep() {
    const step = this.intakeForm.get(`${this.step}`);
    if (step && !step.valid) {
      return;
    }
    if (this.step < 11) {
      this.step++;
    } else {
      this.submitForm();
    }
  }

  previousStep() {
    this.step--;
  }

  submitForm() {
    this.saving = true;
    this.intakeFormService.submitIntakeForm(this.flattenObj(this.intakeForm.value)).subscribe(() => {
      this.saving = false;
      this.router.navigateByUrl('/anonymous-applicant/soa-request', {
        state: {
          firstName: this.intakeForm.get([1, 'firstName'] as const).value,
          lastName: this.intakeForm.get([1, 'lastName'] as const).value,
          email: this.intakeForm.get([1, 'email'] as const).value,
          phone: this.intakeForm.get([3, 'phone'] as const).value,
          zip: this.intakeForm.get([3, 'zip'] as const).value,
          fips: this.intakeForm.get([3, 'fips'] as const).value,
          county: this.intakeForm.get([3, 'county'] as const).value,
          state: this.intakeForm.get([3, 'state'] as const).value,
        },
      });
    });
  }

  flattenObj(obj: any, res: any = {}) {
    for (const key in obj) {
      if (typeof obj[key] === 'object') {
        this.flattenObj(obj[key], res);
      } else {
        res[key] = obj[key];
      }
    }
    return res;
  }

  ngOnDestroy() {
    this.subscription && this.subscription.unsubscribe();
  }
}
